import React, { useState, useEffect, useCallback } from 'react';
import { ClickableImage, ID_PLACEHOLDER, SELFIE_PLACEHOLDER } from './ImagePreview';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import {
  ShieldAlert, Search, Loader2, CheckCircle, XCircle, ChevronLeft, ChevronRight,
  ImageOff, Clock
} from 'lucide-react';
import { ExportListButtons } from './ExportListButtons';
import { toast } from 'sonner';

const STATUS_TABS = [
  { v: 'pending', l: 'Pending', color: 'text-[#FFEA00]' },
  { v: 'whitelisted', l: 'Approved', color: 'text-[#00E676]' },
  { v: 'rejected', l: 'Rejected', color: 'text-[#FF1744]' },
  { v: 'superseded', l: 'Auto-resolved', color: 'text-[#5C7599]' },
];

export function AdminVerificationFailuresPage() {
  const { api } = useAuth();
  const [failures, setFailures] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, whitelisted: 0, rejected: 0, superseded: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selected, setSelected] = useState(null);
  const limit = 10;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, status: statusFilter });
      if (search) params.append('search', search);
      const { data } = await api.get(`/api/admin/verification-failures?${params}`);
      setFailures(data.failures || []);
      setTotal(data.total || 0);
      setCounts(data.counts || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, page, statusFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [statusFilter, search]);

  const openDetail = (f) => { setSelected(f); setDetailOpen(true); };

  const handleWhitelist = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const { data } = await api.post(`/api/admin/verification-failures/${selected.id}/whitelist`);
      toast.success(`Account created for ${data.email}`);
      setDetailOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Whitelist failed');
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await api.post(`/api/admin/verification-failures/${selected.id}/reject`, { reason: rejectReason || 'Verification could not be confirmed' });
      toast.success('Rejected');
      setRejectOpen(false);
      setDetailOpen(false);
      setRejectReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
    setActionLoading(false);
  };

  const totalPages = Math.ceil(total / limit) || 1;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-PH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-verification-failures-page">
        <div className="flex items-start sm:items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-outfit text-3xl font-semibold text-white flex items-center gap-3">
              <ShieldAlert className="w-8 h-8 text-[#FFEA00]" /> Verification Failures
            </h1>
            <p className="text-[#A0B4CC] mt-1">Review failed ID verifications. Whitelist legitimate users whose name format confused the OCR.</p>
          </div>
          <ExportListButtons api={api} resource="verification_failures" filename="verification_failures" />
        </div>

        {/* Status tabs with counts */}
        <div className="flex gap-2 flex-wrap mb-4">
          {STATUS_TABS.map(t => (
            <button
              key={t.v} onClick={() => setStatusFilter(t.v)}
              data-testid={`vf-tab-${t.v}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === t.v ? 'bg-[#041A3B] border border-[#00E5FF]/40 text-white' : 'bg-[#041A3B]/50 border border-white/10 text-[#A0B4CC] hover:border-white/30'
              }`}
            >
              <span className={t.color}>{t.l}</span>
              <span className="text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{counts[t.v] || 0}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
          <Input
            placeholder="Search by email, name, or ID name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-[#041A3B] border-white/10 text-white h-10"
            data-testid="vf-search"
          />
        </div>

        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>
            ) : failures.length === 0 ? (
              <div className="text-center py-16" data-testid="vf-empty">
                <ShieldAlert className="w-12 h-12 text-[#5C7599] mx-auto mb-3 opacity-60" />
                <p className="text-[#A0B4CC]">No {statusFilter} verification failures</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {failures.map(f => (
                  <button
                    key={f.id}
                    onClick={() => openDetail(f)}
                    data-testid={`vf-row-${f.id}`}
                    className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/5 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#000B18] flex-shrink-0 overflow-hidden">
                      <ClickableImage
                        src={f.selfie_url}
                        fallbackSrc={SELFIE_PLACEHOLDER}
                        alt="Selfie"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-medium truncate">{f.name}</p>
                        <span className="text-[#5C7599] text-xs">·</span>
                        <p className="text-[#A0B4CC] text-xs truncate">{f.email}</p>
                      </div>
                      <p className="text-[#FFEA00] text-xs mt-0.5">
                        ID says: <span className="font-mono">{f.id_name_extracted || '(not detected)'}</span>
                      </p>
                      <p className="text-[#5C7599] text-xs mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {fmtDate(f.created_at)} · {f.failure_reason || 'No reason'}
                      </p>
                    </div>
                    {f.is_merchant && <Badge className="bg-[#00E676]/15 text-[#00E676]">Merchant</Badge>}
                  </button>
                ))}
              </div>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-white/5">
                <p className="text-[#5C7599] text-xs">Page {page} of {totalPages} · {total} total</p>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-[#A0B4CC]" data-testid="vf-prev-page">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-[#A0B4CC]" data-testid="vf-next-page">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-[#FFEA00]" /> Verification Failure Detail
              </DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[#000B18] border border-white/10">
                    <p className="text-[#5C7599] text-xs mb-1">Registered Name</p>
                    <p className="text-white font-medium" data-testid="vf-detail-name">{selected.name}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#FFEA00]/5 border border-[#FFEA00]/30">
                    <p className="text-[#FFEA00] text-xs mb-1">Name on ID (OCR)</p>
                    <p className="text-white font-medium font-mono">{selected.id_name_extracted || '(not detected)'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[#5C7599] text-xs mb-1">Email</p>
                    <p className="text-white text-sm break-all">{selected.email}</p>
                  </div>
                  <div>
                    <p className="text-[#5C7599] text-xs mb-1">Mobile</p>
                    <p className="text-white text-sm">{selected.mobile || '—'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[#5C7599] text-xs mb-1">Failure Reason</p>
                  <p className="text-[#FF1744] text-sm">{selected.failure_reason || 'No reason given'}</p>
                  <p className="text-[#5C7599] text-xs mt-1">Confidence: {((selected.confidence || 0) * 100).toFixed(0)}%</p>
                </div>

                {/* Always render both photo slots — fall back to placeholders when missing/broken */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[#5C7599] text-xs mb-1">ID Photo</p>
                    <ClickableImage
                      src={selected.id_photo_url}
                      fallbackSrc={ID_PLACEHOLDER}
                      label={`ID Photo — ${selected.name || ''}`}
                      alt="ID"
                      className="w-full h-48 object-cover rounded-lg border border-white/10"
                      data-testid="vf-id-photo"
                    />
                  </div>
                  <div>
                    <p className="text-[#5C7599] text-xs mb-1">Selfie</p>
                    <ClickableImage
                      src={selected.selfie_url}
                      fallbackSrc={SELFIE_PLACEHOLDER}
                      label={`Selfie — ${selected.name || ''}`}
                      alt="Selfie"
                      className="w-full h-48 object-cover rounded-lg border border-white/10"
                      data-testid="vf-selfie-photo"
                    />
                  </div>
                </div>

                {selected.is_merchant && (
                  <div className="p-3 rounded-lg bg-[#00E676]/5 border border-[#00E676]/20">
                    <p className="text-[#00E676] text-xs font-medium mb-1">Merchant Registration</p>
                    <p className="text-white text-sm">{selected.business_name || '—'}</p>
                    {selected.business_address && <p className="text-[#A0B4CC] text-xs">{selected.business_address}</p>}
                  </div>
                )}

                <div className="p-3 rounded-lg bg-[#5C7599]/5 border border-white/10 text-[#A0B4CC] text-xs">
                  Submitted: {fmtDate(selected.created_at)}
                  {selected.reviewed_at && <><br />Reviewed: {fmtDate(selected.reviewed_at)}</>}
                  {selected.rejection_reason && <><br />Rejection reason: <span className="text-[#FF1744]">{selected.rejection_reason}</span></>}
                </div>
              </div>
            )}
            <DialogFooter>
              {selected?.status === 'pending' && (
                <>
                  <Button variant="ghost" onClick={() => setRejectOpen(true)} className="text-[#FF1744] hover:bg-[#FF1744]/10" data-testid="vf-reject-btn">
                    <XCircle className="w-4 h-4 mr-1.5" /> Reject
                  </Button>
                  <Button onClick={handleWhitelist} disabled={actionLoading} className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90" data-testid="vf-whitelist-btn">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1.5" /> Whitelist & Create Account</>}
                  </Button>
                </>
              )}
              {selected?.status !== 'pending' && (
                <Button variant="ghost" onClick={() => setDetailOpen(false)} className="text-[#A0B4CC]">Close</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Reason Dialog */}
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#FF1744]">Reject Verification</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label className="text-[#A0B4CC] text-sm">Reason</Label>
              <textarea
                value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                rows={3} placeholder="e.g., Fake ID, mismatched photo, suspected fraud..."
                className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm resize-none focus:border-[#FF1744] focus:outline-none"
                data-testid="vf-reject-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRejectOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
              <Button onClick={handleReject} disabled={actionLoading} className="bg-[#FF1744] text-white hover:bg-[#FF1744]/90" data-testid="vf-confirm-reject-btn">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Rejection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminSidebarLayout>
  );
}
